/**
 * Phone number utilities for SMS text alerts and player self-scoring
 */

/**
 * Formats a phone number to display format: (XXX) XXX-XXXX
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number
 */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Handle different lengths
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US numbers with country code
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if doesn't match expected format
  return phone;
}

/**
 * Formats phone number for storage (E.164 format): +[countryCode][number]
 * @param {string} phone - Raw phone number
 * @param {string} countryCode - Country code with + prefix (e.g., '+1', '+44')
 * @returns {string} E.164 formatted phone number
 */
export function formatPhoneE164(phone, countryCode = '+1') {
  if (!phone) return '';

  // Remove all non-numeric characters from phone
  const cleaned = phone.replace(/\D/g, '');

  // If no digits, return empty
  if (!cleaned) return '';

  // If a country code is provided, use it
  if (countryCode) {
    // Ensure country code has + prefix
    const normalizedCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
    // Remove country code from cleaned number if it starts with it (without +)
    const codeDigits = normalizedCode.replace(/\D/g, '');
    if (cleaned.startsWith(codeDigits)) {
      return `${normalizedCode}${cleaned.slice(codeDigits.length)}`;
    }
    return `${normalizedCode}${cleaned}`;
  }

  // Legacy behavior: Add US country code if not present
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // Return with + prefix if already has country code
  return phone.startsWith('+') ? phone : `+${cleaned}`;
}

/**
 * Validates a US phone number
 * @param {string} phone - Phone number to validate
 * @returns {{ valid: boolean, error?: string }} Validation result
 */
export function validatePhone(phone) {
  if (!phone || !phone.trim()) {
    return { valid: false, error: 'Phone number is required' };
  }

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Check length
  if (cleaned.length < 10) {
    return { valid: false, error: 'Phone number is too short' };
  }

  if (cleaned.length > 11) {
    return { valid: false, error: 'Phone number is too long' };
  }

  // If 11 digits, must start with 1 (US country code)
  if (cleaned.length === 11 && !cleaned.startsWith('1')) {
    return { valid: false, error: 'Invalid US phone number' };
  }

  // Get the 10-digit number (without country code)
  const tenDigit = cleaned.length === 11 ? cleaned.slice(1) : cleaned;

  // Area code cannot start with 0 or 1
  if (tenDigit.startsWith('0') || tenDigit.startsWith('1')) {
    return { valid: false, error: 'Invalid area code' };
  }

  // Exchange code (4th-6th digits) cannot start with 0 or 1
  if (tenDigit.charAt(3) === '0' || tenDigit.charAt(3) === '1') {
    return { valid: false, error: 'Invalid phone number' };
  }

  return { valid: true };
}

/**
 * Auto-formats phone input as user types
 * @param {string} input - Current input value
 * @param {string} previousValue - Previous input value (for backspace handling)
 * @returns {string} Formatted input
 */
export function formatPhoneInput(input, previousValue = '') {
  if (!input) return '';

  // Remove all non-numeric characters
  const cleaned = input.replace(/\D/g, '');

  // Limit to 10 digits (ignore country code in input)
  const limited = cleaned.slice(0, 10);

  // Format progressively as user types
  if (limited.length === 0) {
    return '';
  } else if (limited.length <= 3) {
    return `(${limited}`;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  } else {
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
}

/**
 * Creates a masked phone number for display (privacy)
 * Shows only last 4 digits: ***-****-1234
 * @param {string} phone - Full phone number
 * @returns {string} Masked phone number
 */
export function maskPhone(phone) {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Get last 4 digits
  const lastFour = cleaned.slice(-4);

  return `***-***-${lastFour}`;
}

/**
 * Extracts clean digits from a formatted phone number
 * @param {string} phone - Formatted phone number
 * @returns {string} Clean digits only
 */
export function getPhoneDigits(phone) {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}
